import React, { useEffect, useRef, useState } from 'react';
import './CotiousWebsiteApp.css';

/*
 * Page d'accueil Cotious.
 *
 * Le mouvement est réservé à trois usages : la parallaxe de la scène produit
 * du héros, l'apparition des sections à l'entrée dans la fenêtre, et les
 * compteurs du tableau de bord. Sans JavaScript, tout est visible : la classe
 * `js` n'est posée sur <html> qu'au montage, et c'est elle qui autorise les
 * états masqués.
 */

const Check = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.15" />
    <path d="m7.5 12.5 3 3 6-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Arrow = () => (
  <svg className="ct-btn__arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Met en gras la partie du titre entourée d'astérisques. */
const Emphasis = ({ text }) => {
  const parts = String(text || '').split(/\*(.+?)\*/);
  return parts.map((part, index) => (index % 2 === 1 ? <em key={index}>{part}</em> : part));
};

const formatNumber = (value) => new Intl.NumberFormat('fr-FR').format(Math.round(value));

/* Compteur : monte de zéro à la cible la première fois que l'élément entre
   dans la fenêtre. */
const CountUp = ({ to, suffix = '', duration = 1400 }) => {
  const [value, setValue] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setValue(to); return undefined; }

    let frame;
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        setValue(to * eased);
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    observer.observe(node);
    return () => { observer.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [to, duration]);

  return <span ref={ref}>{formatNumber(value)}{suffix}</span>;
};

/* ── Écrans produit ────────────────────────────────────────────────── */

const LEDGER = [
  { ref: 'CTN-2418', bien: 'Villa Peyrolles', montant: '1 500 €', etat: 'Retenue', tone: 'held' },
  { ref: 'CTN-2417', bien: 'Studio Bastide', montant: '600 €', etat: 'Libérée', tone: 'released' },
  { ref: 'CTN-2416', bien: 'Mas des Oliviers', montant: '2 000 €', etat: 'En attente', tone: 'waiting' },
  { ref: 'CTN-2414', bien: 'Appartement Carnot', montant: '800 €', etat: 'Libérée', tone: 'released' },
  { ref: 'CTN-2411', bien: 'Chalet des Crêtes', montant: '1 200 €', etat: 'Litige', tone: 'dispute' },
];

const CHART = [38, 52, 46, 64, 58, 72, 66, 84, 78, 92, 88, 100];

const Dashboard = React.forwardRef((props, ref) => (
  <div className="ct-window" ref={ref} aria-label="Aperçu du tableau de bord Cotious">
    <aside className="ct-window__side">
      <div className="ct-window__brand">Cotious</div>
      <div className="ct-window__nav is-active"><i />Cautions</div>
      <div className="ct-window__nav"><i />Biens</div>
      <div className="ct-window__nav"><i />Locataires</div>
      <div className="ct-window__nav"><i />Litiges</div>
      <div className="ct-window__nav"><i />Intégrations</div>
    </aside>
    <div className="ct-window__main">
      <div className="ct-window__head">
        <div className="ct-window__title">Cautions<small>Saison 2026 · 37 biens</small></div>
        <div className="ct-window__pill">Nouvelle caution</div>
      </div>
      <div className="ct-kpis">
        <div className="ct-kpi"><div className="ct-kpi__label">Cautions actives</div><div className="ct-kpi__value"><CountUp to={128} /></div><div className="ct-kpi__delta">+14 cette semaine</div></div>
        <div className="ct-kpi"><div className="ct-kpi__label">Montant retenu</div><div className="ct-kpi__value"><CountUp to={84250} suffix=" €" /></div><div className="ct-kpi__delta">0 € débité</div></div>
        <div className="ct-kpi"><div className="ct-kpi__label">Libérées ce mois</div><div className="ct-kpi__value"><CountUp to={46} /></div><div className="ct-kpi__delta">en moyenne 2 h après le départ</div></div>
        <div className="ct-kpi"><div className="ct-kpi__label">Litiges ouverts</div><div className="ct-kpi__value"><CountUp to={2} /></div><div className="ct-kpi__delta ct-kpi__delta--warn">à traiter</div></div>
      </div>
      <div className="ct-window__grid">
        <div className="ct-panel">
          <div className="ct-panel__title">Dernières cautions <span>aujourd&apos;hui</span></div>
          <table className="ct-table">
            <thead><tr><th>Réf.</th><th>Bien</th><th>État</th><th>Montant</th></tr></thead>
            <tbody>
              {LEDGER.map((row) => (
                <tr key={row.ref}>
                  <td>{row.ref}</td>
                  <td>{row.bien}</td>
                  <td><span className={`ct-status ct-status--${row.tone}`}>{row.etat}</span></td>
                  <td>{row.montant}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="ct-panel">
          <div className="ct-panel__title">Empreintes posées <span>12 semaines</span></div>
          <div className="ct-chart" aria-hidden="true">
            {CHART.map((height, index) => <i key={index} className={index >= 9 ? 'is-hot' : ''} style={{ height: `${height}%` }} />)}
          </div>
        </div>
      </div>
    </div>
  </div>
));

const Phone = React.forwardRef((props, ref) => (
  <div className="ct-phone" ref={ref} aria-label="Aperçu du parcours locataire sur mobile">
    <div className="ct-phone__screen">
      <div className="ct-phone__brand">Cotious</div>
      <div>
        <div className="ct-phone__prop">Villa Peyrolles · 12 au 19 juillet</div>
        <div className="ct-phone__amount" style={{ marginTop: 6 }}>1 500 €</div>
      </div>
      <div className="ct-phone__row"><span>Partage</span><b>Vous seul</b></div>
      <div className="ct-phone__row"><span>Moyen de paiement</span><b>Carte ···· 4412</b></div>
      <div className="ct-phone__row"><span>Frais de dossier</span><b>28,50 €</b></div>
      <div className="ct-phone__ok"><Check size={18} /><span>Empreinte posée. Le montant de la caution n&apos;est pas débité.</span></div>
      <div className="ct-phone__btn">Terminé</div>
    </div>
  </div>
));

const Toast = React.forwardRef(({ title, text }, ref) => (
  <div className="ct-toast" ref={ref}>
    <div className="ct-toast__icon"><Check size={22} /></div>
    <div><b>{title}</b><span>{text}</span></div>
  </div>
));

/* Écrans du parcours, un par étape et par point de vue. */
const JOURNEY_SCREENS = {
  owner: [
    { tag: 'Espace propriétaire', title: 'Nouvelle caution', items: [['Bien', 'Villa Peyrolles'], ['Montant', '1 500 €'], ['Locataire', 'm.durand@…']], done: [] },
    { tag: 'Envoi automatique', title: 'Le locataire est prévenu', items: [['E-mail 1', 'Partager ou non'], ['E-mail 2', 'Moyen de paiement'], ['Relance', 'J+2 si silence']], done: [0] },
    { tag: 'Tableau de bord', title: 'Suivi en temps réel', items: [['CTN-2418', 'Retenue'], ['CTN-2417', 'Libérée'], ['CTN-2416', 'En attente']], done: [1] },
    { tag: 'Fin de séjour', title: 'Libérer ou retenir', items: [['État des lieux', 'Sans réserve'], ['Action', 'Libérer la caution'], ['Notification', 'Envoyée au locataire']], done: [0, 1, 2] },
  ],
  tenant: [
    { tag: 'Réception', title: 'Une demande de caution', items: [['Bien', 'Villa Peyrolles'], ['Montant', '1 500 €'], ['Validité', '12 au 19 juillet']], done: [] },
    { tag: 'Partage', title: 'Seul ou à plusieurs', items: [['Vous', '750 €'], ['Camille', '750 €'], ['Lien envoyé', 'à Camille']], done: [0] },
    { tag: 'Paiement', title: 'Empreinte sécurisée', items: [['Moyen', 'Carte ···· 4412'], ['Frais', '1,9 %, carte ou SEPA'], ['Débit de la caution', 'Aucun']], done: [0, 1, 2] },
    { tag: 'Séjour', title: 'Rien à faire', items: [['Caution', 'Sécurisée'], ['Plafond carte', 'Intact'], ['Action requise', 'Aucune']], done: [0, 1, 2] },
    { tag: 'Départ', title: 'Caution libérée', items: [['État des lieux', 'Validé'], ['Retenue', '0 €'], ['Empreinte', 'Levée']], done: [0, 1, 2] },
  ],
};

const JourneyScreen = ({ view, index }) => {
  const screens = JOURNEY_SCREENS[view] || [];
  const screen = screens[Math.min(index, screens.length - 1)];
  if (!screen) return null;
  return (
    <div className="ct-screen">
      <div className="ct-screen__card" key={`${view}-${index}`}>
        <span className="ct-screen__tag">{screen.tag}</span>
        <h4>{screen.title}</h4>
        <div className="ct-screen__list">
          {screen.items.map(([label, value], i) => (
            <div className={`ct-screen__item ${screen.done.includes(i) ? 'is-done' : ''}`} key={label}>
              <span>{label}</span><b>{value}</b>
            </div>
          ))}
        </div>
        <p>Étape {index + 1} sur {screens.length}</p>
      </div>
    </div>
  );
};

/* ── Page ──────────────────────────────────────────────────────────── */

const CotiousWebsiteApp = ({
  product_name, headline, subheadline, navigation, benefits, testimonials, partners,
  articles, faqs, app_url, logo_blue_url, logo_white_url, steps, pricing, social_proof,
  dual_arguments, sectors,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [journeyView, setJourneyView] = useState('owner');
  const [activeStep, setActiveStep] = useState(0);
  const [activeSector, setActiveSector] = useState(0);

  const dashRef = useRef(null);
  const phoneRef = useRef(null);
  const toastRef = useRef(null);
  const stepRefs = useRef([]);

  /* Parallaxe du héros et ombre de la barre. */
  useEffect(() => {
    document.documentElement.classList.add('js');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      setScrolled(y > 24);
      if (reduced || y > 1400 || window.innerWidth < 761) return;
      if (dashRef.current) dashRef.current.style.transform = `translate3d(0, ${y * 0.08}px, 0)`;
      if (phoneRef.current) phoneRef.current.style.transform = `translate3d(0, ${y * -0.16}px, 0)`;
      if (toastRef.current) toastRef.current.style.transform = `translate3d(0, ${y * -0.06}px, 0)`;
    };
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(update); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* Apparition des sections. */
  useEffect(() => {
    const nodes = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); observer.unobserve(entry.target); }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  /* Étape active du parcours, pilotée par le défilement. */
  useEffect(() => {
    const nodes = stepRefs.current.filter(Boolean);
    if (!nodes.length) return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveStep(Number(entry.target.dataset.index));
      });
    }, { rootMargin: '-40% 0px -45% 0px', threshold: 0 });
    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [journeyView]);

  const switchJourney = (view) => { setJourneyView(view); setActiveStep(0); };
  const currentSteps = (steps && steps[journeyView]) || [];
  const sectorItems = (sectors && sectors.items) || [];
  const sector = sectorItems[activeSector];

  return (
    <div className="ct">
      <nav className={`ct-nav ${scrolled ? 'ct-nav--scrolled' : ''}`}>
        <div className="ct-container ct-nav__inner">
          <a href="/" className="ct-nav__logo" aria-label={product_name}><img src={logo_blue_url} alt={product_name} /></a>
          <div className={`ct-nav__links ${mobileOpen ? 'ct-nav__links--open' : ''}`}>
            {navigation.map((item) => <a key={item.href} href={item.href} onClick={() => setMobileOpen(false)}>{item.label}</a>)}
          </div>
          <a className="ct-btn ct-btn--primary ct-btn--sm ct-nav__cta" href={app_url}>Accéder à l&apos;application</a>
          <button className={`ct-nav__burger ${mobileOpen ? 'ct-nav__burger--open' : ''}`} aria-label="Ouvrir le menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen((open) => !open)}>
            <span /><span /><span />
          </button>
        </div>
      </nav>

      <main>
        {/* Héros ---------------------------------------------------------- */}
        <section className="ct-hero" id="top">
          <div className="ct-container ct-container--wide">
            <div className="ct-hero__copy">
              <p className="ct-eyebrow">Dépôts de garantie pour la location courte durée</p>
              <h1><Emphasis text={headline} /></h1>
              <p className="ct-hero__sub">{subheadline}</p>
              <div className="ct-actions ct-hero__actions">
                <a className="ct-btn ct-btn--primary ct-btn--lg" href={app_url}>Accéder à l&apos;application <Arrow /></a>
                <a className="ct-btn ct-btn--ghost ct-btn--lg" href="#fonctionnement">Voir le fonctionnement</a>
              </div>
              <div className="ct-hero__note">
                <span><Check size={18} />Sans abonnement pour le propriétaire</span>
                <span><Check size={18} />Aucun débit pour le voyageur</span>
                <span><Check size={18} />Mise en place en une journée</span>
              </div>
            </div>

            <div className="ct-stage">
              <div className="ct-stage__dash" data-parallax ref={dashRef}><Dashboard /></div>
              <div className="ct-stage__phone" data-parallax ref={phoneRef}><Phone /></div>
              <div className="ct-stage__toast" data-parallax ref={toastRef}>
                <Toast title="Caution CTN-2417 libérée" text="600 € · Studio Bastide · il y a 2 min" />
              </div>
            </div>
          </div>
        </section>

        <div className="ct-trust">
          <div className="ct-container ct-trust__inner">
            <span><Check />Établissement de paiement agréé</span>
            <span><Check />Empreinte bancaire sans débit</span>
            <span><Check />Assistance 24/24</span>
            <span><Check />Connexion à votre PMS</span>
            <span><Check />Partage entre voyageurs</span>
          </div>
        </div>

        {/* Produit -------------------------------------------------------- */}
        <section className="ct-section" id="produit">
          <div className="ct-container">
            <header className="ct-sec-head reveal">
              <p className="ct-eyebrow">Le produit</p>
              <h2>Tout ce qu&apos;une caution vous demande, pris en charge.</h2>
              <p>De la demande à la libération, sans chèque à garder, sans virement à rendre, sans tableur à tenir.</p>
            </header>

            <div className="ct-bento">
              <article className="ct-tile ct-tile--7 reveal">
                <div>
                  <h3>{benefits[1] ? benefits[1].title : 'Un registre unique pour tous vos biens'}</h3>
                  <p style={{ marginTop: 10 }}>{benefits[1] ? benefits[1].body : ''}</p>
                </div>
                <div className="ct-tile__visual ct-mini">
                  <div className="ct-mini__rows">
                    {LEDGER.slice(0, 4).map((row) => (
                      <div className="ct-mini__row" key={row.ref}>
                        <span>{row.bien}</span>
                        <span className={`ct-status ct-status--${row.tone}`}>{row.etat}</span>
                        <b>{row.montant}</b>
                      </div>
                    ))}
                  </div>
                </div>
              </article>

              <article className="ct-tile ct-tile--5 ct-tile--navy reveal reveal--delay-1">
                <div>
                  <h3>{benefits[0] ? benefits[0].title : 'Plus de chèque à garder ni de virement à rendre'}</h3>
                  <p style={{ marginTop: 10 }}>{benefits[0] ? benefits[0].body : ''}</p>
                </div>
                <div className="ct-tile__visual ct-mini">
                  <div className="ct-mini__big">0 €</div>
                  <div className="ct-mini__cap">débité sur 84 250 € retenus cette saison</div>
                  <div className="ct-mini__rows" style={{ marginTop: 14 }}>
                    <div className="ct-mini__row"><span>Empreintes posées</span><b>128</b></div>
                    <div className="ct-mini__row"><span>Chèques reçus</span><b>0</b></div>
                  </div>
                </div>
              </article>

              <article className="ct-tile ct-tile--12 reveal reveal--delay-2">
                <div className="ct-journey" style={{ gap: 32 }}>
                  <div>
                    <h3>{benefits[2] ? benefits[2].title : 'Retenir le montant réel d’un dommage'}</h3>
                    <p style={{ marginTop: 10 }}>{benefits[2] ? benefits[2].body : ''}</p>
                  </div>
                  <div className="ct-mini" style={{ background: '#fff' }}>
                    <div className="ct-mini__cap" style={{ marginTop: 0 }}>Montant à retenir sur la caution de 1 500 €</div>
                    <div className="ct-mini__big" style={{ marginTop: 8 }}>420 €</div>
                    <div className="ct-slider" aria-hidden="true"><i /><b /></div>
                    <div className="ct-slider__scale"><span>0 €</span><span>1 500 €</span></div>
                    <div className="ct-mini__rows" style={{ marginTop: 14 }}>
                      <div className="ct-mini__row"><span>Motif</span><b>Vitre du salon</b></div>
                      <div className="ct-mini__row"><span>Accord du locataire</span><span className="ct-status ct-status--released">Signé</span></div>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Fonctionnement ------------------------------------------------- */}
        <section className="ct-section ct-section--ice" id="fonctionnement">
          <div className="ct-container">
            <header className="ct-sec-head reveal">
              <p className="ct-eyebrow">Fonctionnement</p>
              <h2>Le même dossier, vu des deux côtés.</h2>
              <p>Ce que fait le gestionnaire, ce que voit le voyageur. Cliquez sur une étape ou laissez défiler.</p>
            </header>

            <div className="ct-switch" role="tablist" aria-label="Point de vue">
              <button type="button" role="tab" aria-selected={journeyView === 'owner'} className={journeyView === 'owner' ? 'is-active' : ''} onClick={() => switchJourney('owner')}>Côté propriétaire</button>
              <button type="button" role="tab" aria-selected={journeyView === 'tenant'} className={journeyView === 'tenant' ? 'is-active' : ''} onClick={() => switchJourney('tenant')}>Côté locataire</button>
            </div>

            <div className="ct-journey">
              <div className="ct-steps">
                {currentSteps.map((step, index) => (
                  <div
                    key={`${journeyView}-${index}`}
                    className={`ct-step ${activeStep === index ? 'is-active' : ''}`}
                    data-index={index}
                    ref={(node) => { stepRefs.current[index] = node; }}
                    onClick={() => setActiveStep(index)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setActiveStep(index); } }}
                  >
                    <div className="ct-step__num">{index + 1}</div>
                    <div>
                      <h3>{step.title}</h3>
                      <p>{step.body}</p>
                      {step.detail ? <p className="ct-step__detail">{step.detail}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
              <div className="ct-journey__visual">
                <JourneyScreen view={journeyView} index={activeStep} />
              </div>
            </div>
          </div>
        </section>

        {/* Chiffres et comparaison ---------------------------------------- */}
        <section className="ct-section ct-section--navy" id="chiffres">
          <div className="ct-container">
            <header className="ct-sec-head reveal">
              <p className="ct-eyebrow">Ce que change l&apos;empreinte bancaire</p>
              <h2>Le chèque de caution a trois défauts. L&apos;empreinte n&apos;en a aucun.</h2>
            </header>

            <div className="ct-facts reveal">
              <div className="ct-fact"><div className="ct-fact__value">0 €</div><div className="ct-fact__label">pour poser une caution, sans abonnement</div></div>
              <div className="ct-fact"><div className="ct-fact__value">1,9 %</div><div className="ct-fact__label">de frais de dossier pour le locataire, carte ou SEPA</div></div>
              <div className="ct-fact"><div className="ct-fact__value">5 %</div><div className="ct-fact__label">seulement sur une somme que vous retenez</div></div>
              <div className="ct-fact"><div className="ct-fact__value">24/24</div><div className="ct-fact__label">assistance, pour vous et vos voyageurs</div></div>
            </div>

            <div className="ct-compare reveal reveal--delay-1">
              <div className="ct-compare__wrap">
                <table>
                  <thead>
                    <tr><th>Moment</th><th>Chèque de caution</th><th>Virement</th><th className="is-us">Empreinte Cotious</th></tr>
                  </thead>
                  <tbody>
                    <tr><td>Avant l&apos;arrivée</td><td>Rien n&apos;est sécurisé</td><td>Le voyageur avance la somme</td><td className="is-us">Empreinte posée en deux minutes, sans débit</td></tr>
                    <tr><td>À l&apos;arrivée</td><td>Chèque à réceptionner, parfois oublié</td><td>Virement à vérifier sur le relevé</td><td className="is-us">Rien à faire, l&apos;état est visible dans le registre</td></tr>
                    <tr><td>Pendant le séjour</td><td>Chèque à conserver</td><td>Somme immobilisée sur votre compte</td><td className="is-us">Montant réservé chez la banque du voyageur</td></tr>
                    <tr><td>Au départ</td><td>Chèque à rendre ou à détruire</td><td>Virement retour à saisir</td><td className="is-us">Libération en un clic, voyageur notifié</td></tr>
                    <tr><td>En cas de dommage</td><td>Encaissement total, puis remboursement partiel</td><td>Remboursement partiel à calculer</td><td className="is-us">Seul le montant convenu est prélevé, accord tracé</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* Secteurs ------------------------------------------------------- */}
        {sectorItems.length ? (
          <section className="ct-section" id="secteurs">
            <div className="ct-container">
              <header className="ct-sec-head reveal">
                <p className="ct-eyebrow">Secteurs</p>
                <h2>{sectors.title}</h2>
                <p>{sectors.subtitle}</p>
              </header>
              <div className="ct-tabs reveal" role="tablist">
                {sectorItems.map((item, index) => (
                  <button type="button" role="tab" key={item.name} aria-selected={activeSector === index} className={activeSector === index ? 'is-active' : ''} onClick={() => setActiveSector(index)}>{item.name}</button>
                ))}
              </div>
              {sector ? (
                <div className="ct-sector" key={sector.name}>
                  <div className="ct-sector__intro">
                    <h3>{sector.name}</h3>
                    <p>{sector.tagline}</p>
                  </div>
                  <div className="ct-sector__body">
                    <div className="ct-sector__block"><h4>Ce qui coince aujourd&apos;hui</h4><p>{sector.problem}</p></div>
                    <div className="ct-sector__block ct-sector__block--solution"><h4>Ce que Cotious change</h4><p>{sector.solution}</p></div>
                    <div className="ct-actions"><a className="ct-btn ct-btn--primary" href={app_url}>Accéder à l&apos;application <Arrow /></a></div>
                  </div>
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* Avantages ------------------------------------------------------ */}
        {dual_arguments ? (
          <section className="ct-section ct-section--ice" id="avantages">
            <div className="ct-container">
              <header className="ct-sec-head reveal">
                <p className="ct-eyebrow">Avantages</p>
                <h2>{dual_arguments.title}</h2>
                <p>{dual_arguments.subtitle}</p>
              </header>
              <div className="ct-pricing">
                {[dual_arguments.pro, dual_arguments.tenant].map((side, sideIndex) => (
                  <div className={`ct-price ${sideIndex === 0 ? 'ct-price--navy' : ''} reveal ${sideIndex === 1 ? 'reveal--delay-1' : ''}`} key={side.label}>
                    <div className="ct-price__who">{side.label}</div>
                    <div className="ct-steps" style={{ gap: 10 }}>
                      {side.items.map((item, index) => (
                        <div key={item.title} style={{ display: 'grid', gridTemplateColumns: '36px minmax(0, 1fr)', gap: 14 }}>
                          <div className="ct-step__num" style={{ width: 36, height: 36, borderRadius: 10, fontSize: '0.875rem', background: sideIndex === 0 ? 'rgba(255,255,255,0.12)' : undefined, color: sideIndex === 0 ? '#fff' : undefined }}>{index + 1}</div>
                          <div>
                            <div style={{ fontWeight: 800, letterSpacing: '-0.01em' }}>{item.title}</div>
                            <div className="ct-price__sub" style={{ fontSize: '0.9875rem', marginTop: 4 }}>{item.body}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        {/* Tarifs --------------------------------------------------------- */}
        {pricing ? (
          <section className="ct-section" id="tarifs">
            <div className="ct-container">
              <header className="ct-sec-head ct-sec-head--center reveal">
                <p className="ct-eyebrow" style={{ justifyContent: 'center' }}>Tarifs</p>
                <h2>{pricing.title}</h2>
                <p>{pricing.subtitle}</p>
              </header>
              <div className="ct-pricing">
                <div className="ct-price ct-price--navy reveal">
                  <div className="ct-price__who">{pricing.owner_label}</div>
                  <div className="ct-price__amount"><b>{pricing.owner_price}</b><span>{pricing.owner_price_caption}</span></div>
                  <p className="ct-price__sub">{pricing.owner_sublabel}</p>
                  <ul className="ct-checklist">
                    {pricing.owner_features.map((feature) => <li key={feature}><Check />{feature}</li>)}
                  </ul>
                  <p className="ct-price__note">{pricing.owner_note}</p>
                  <div className="ct-actions" style={{ marginTop: 'auto' }}>
                    <a className="ct-btn ct-btn--light ct-btn--block" href={app_url}>{pricing.cta_text}</a>
                  </div>
                </div>
                <div className="ct-price reveal reveal--delay-1">
                  <div className="ct-price__who">{pricing.tenant_label}</div>
                  <div className="ct-price__rates">
                    <div className="ct-price__rate"><b>{pricing.tenant_rate}</b><span>{pricing.tenant_rate_label}</span></div>
                  </div>
                  <p className="ct-price__sub">{pricing.tenant_sublabel}</p>
                  <ul className="ct-checklist">
                    {pricing.tenant_features.map((feature) => <li key={feature}><Check />{feature}</li>)}
                  </ul>
                  <p className="ct-price__note" style={{ marginTop: 'auto' }}>{pricing.tenant_note}</p>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {/* Preuves -------------------------------------------------------- */}
        {(testimonials && testimonials.length) || (partners && partners.length) ? (
          <section className="ct-section ct-section--ice" id="temoignages">
            <div className="ct-container">
              <header className="ct-sec-head reveal">
                <p className="ct-eyebrow">Ils l&apos;utilisent</p>
                <h2>Des gestionnaires qui ne rappellent plus pour un chèque.</h2>
              </header>
              {testimonials && testimonials.length ? (
                <div className="ct-quotes">
                  {testimonials.map((testimonial, index) => (
                    <figure className={`ct-quote reveal ${index ? `reveal--delay-${index}` : ''}`} key={testimonial.quote}>
                      <div className="ct-quote__mark" aria-hidden="true">“</div>
                      <blockquote>{testimonial.quote}</blockquote>
                      <figcaption className="ct-quote__by">
                        <strong>{testimonial.author_name}</strong>
                        <span>{[testimonial.author_role, testimonial.company_name].filter(Boolean).join(' · ')}</span>
                      </figcaption>
                    </figure>
                  ))}
                </div>
              ) : null}
              {partners && partners.length ? (
                <div className="ct-partners reveal">
                  {partners.map((partner) => (
                    <div className="ct-partner" key={partner.name}>
                      {partner.activity_type ? <div className="ct-partner__kind">{partner.activity_type}</div> : null}
                      <div className="ct-partner__name">{partner.name}</div>
                      {partner.description ? <p>{partner.description}</p> : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {social_proof && social_proof.visible ? (
                <div className="ct-facts reveal" style={{ marginTop: 40 }}>
                  {[['clients_count', 'clients_label'], ['deposits_count', 'deposits_label'], ['satisfaction_rate', 'satisfaction_label'], ['availability', 'availability_label']].map(([value, label]) => (
                    <div className="ct-fact" key={value}><div className="ct-fact__value">{social_proof[value]}</div><div className="ct-fact__label">{social_proof[label]}</div></div>
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        {/* Articles ------------------------------------------------------- */}
        {articles && articles.length ? (
          <section className="ct-section" id="articles">
            <div className="ct-container">
              <header className="ct-sec-head reveal">
                <p className="ct-eyebrow">Ressources</p>
                <h2>Ce que nous écrivons sur le dépôt de garantie.</h2>
              </header>
              <div className="ct-cards">
                {articles.map((article, index) => (
                  <a className={`ct-card reveal ${index ? `reveal--delay-${index}` : ''}`} href={`/articles/${article.slug}`} key={article.slug}>
                    <div className={`ct-card__media ${article.cover_image_url ? '' : 'ct-card__media--empty'}`}>
                      {article.cover_image_url ? <img src={article.cover_image_url} alt="" loading="lazy" /> : <span>{article.title.charAt(0)}</span>}
                    </div>
                    <div className="ct-card__body">
                      <div className="ct-card__meta">{article.published_at}</div>
                      <h3>{article.title}</h3>
                      <p>{article.excerpt}</p>
                      <span className="ct-card__more">Lire l&apos;article <Arrow /></span>
                    </div>
                  </a>
                ))}
              </div>
              <div className="ct-actions" style={{ marginTop: 32 }}>
                <a className="ct-btn ct-btn--ghost" href="/articles">Tous les articles</a>
              </div>
            </div>
          </section>
        ) : null}

        {/* Questions ------------------------------------------------------ */}
        {faqs && faqs.length ? (
          <section className="ct-section ct-section--ice" id="faq">
            <div className="ct-container">
              <header className="ct-sec-head reveal">
                <p className="ct-eyebrow">Questions fréquentes</p>
                <h2>Ce qu&apos;on nous demande avant de basculer.</h2>
              </header>
              <div className="ct-faq reveal">
                {faqs.map((faq) => (
                  <details key={faq.slug || faq.question}>
                    <summary>{faq.question}</summary>
                    <div className="ct-faq__answer" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                  </details>
                ))}
              </div>
              <div className="ct-actions" style={{ marginTop: 28 }}>
                <a className="ct-btn ct-btn--ghost" href="/faq">Toutes les questions</a>
              </div>
            </div>
          </section>
        ) : null}

        {/* Bandeau final -------------------------------------------------- */}
        <section className="ct-cta-band">
          <div className="ct-container ct-cta-band__inner">
            <div className="reveal">
              <p className="ct-eyebrow">Démarrer</p>
              <h2>Sécurisez votre prochaine caution avec Cotious.</h2>
              <p>Créez votre compte et posez votre première empreinte bancaire. Sans abonnement pour le propriétaire, aucun débit pour le voyageur.</p>
              <div className="ct-actions">
                <a className="ct-btn ct-btn--light ct-btn--lg" href={app_url}>Accéder à l&apos;application <Arrow /></a>
                <a className="ct-btn ct-btn--outline-light ct-btn--lg" href="mailto:contact@cotious.fr">Écrire à l&apos;équipe</a>
              </div>
            </div>
            <div className="ct-cta-band__art reveal reveal--delay-1" aria-hidden="true">
              <Toast title="Empreinte posée · CTN-2418" text="1 500 € réservés, 0 € débité" />
              <Toast title="Caution CTN-2417 libérée" text="600 € · Studio Bastide · voyageur notifié" />
              <Toast title="Accord signé · CTN-2411" text="420 € retenus sur 1 200 €, motif tracé" />
            </div>
          </div>
        </section>
      </main>

      <footer className="ct-footer">
        <div className="ct-container ct-footer__inner">
          <div className="ct-footer__brand">
            <a href="/" className="ct-footer__logo" aria-label={product_name}><img src={logo_white_url} alt={product_name} /></a>
            <p>La caution de vos locations, sécurisée avant l&apos;arrivée et jamais débitée.</p>
            <div className="ct-footer__social">
              <a href="https://www.linkedin.com/company/cotious" target="_blank" rel="noreferrer" aria-label="LinkedIn">
                <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
              <a href="mailto:contact@cotious.fr" aria-label="Écrire à Cotious">
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="1.8"/><path d="m22 6-10 7L2 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </a>
            </div>
          </div>
          <div className="ct-footer__links">
            <div className="ct-footer__col">
              <h4>Produit</h4>
              <a href="#produit">Le produit</a>
              <a href="#fonctionnement">Fonctionnement</a>
              <a href="#tarifs">Tarifs</a>
              <a href="#secteurs">Secteurs</a>
            </div>
            <div className="ct-footer__col">
              <h4>Ressources</h4>
              <a href="/articles">Articles</a>
              <a href="/faq">Questions fréquentes</a>
              <a href={app_url}>Accéder à l&apos;application</a>
            </div>
            <div className="ct-footer__col">
              <h4>Contact</h4>
              <a href="mailto:contact@cotious.fr">contact@cotious.fr</a>
              <span>48 rue de Varenne, 75007 Paris</span>
            </div>
          </div>
        </div>
        <div className="ct-container ct-footer__bottom">
          <span>© {new Date().getFullYear()} Cotious. Tous droits réservés.</span>
          <div className="ct-footer__legal">
            <a href="/legal/mentions-legales">Mentions légales</a>
            <a href="/legal/politique-de-confidentialite">Confidentialité</a>
            <a href="/legal/conditions-generales-utilisation">CGU</a>
            <a href="/legal/conditions-generales-vente">CGV</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CotiousWebsiteApp;
